"use client";

import { useState } from "react";
import { Check, ChevronsUpDown, CircleOff } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { useGetStoragePresets } from "../api/getStoragePresets";

/** Searchable single-select over the library's storage presets. `modal` so it
 *  stays interactive inside dialogs (e.g. the folder settings modal). */
export default function PresetPicker({
    value,
    onChange,
    disabled,
}: {
    value: string | null;
    onChange: (presetId: string | null) => void;
    disabled?: boolean;
}) {
    const { dir } = useLocale();
    const [open, setOpen] = useState(false);

    const { data: presets, isPending } = useGetStoragePresets();

    const selected = presets?.find((preset) => preset.id === value);

    return (
        <Popover modal open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    disabled={isPending || disabled}
                    className="h-9 w-full px-2.5 justify-between bg-background font-normal hover:bg-background"
                >
                    <span
                        className={cn(
                            "truncate",
                            !selected && "text-muted-foreground"
                        )}
                    >
                        {selected?.name ??
                            t(
                                "storage.folder.settings-modal.preset.placeholder"
                            )}
                    </span>
                    <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent
                dir={dir}
                align="start"
                className="w-[var(--radix-popover-trigger-width)] p-0"
            >
                <Command>
                    <CommandInput
                        placeholder={t(
                            "storage.folder.settings-modal.preset.search-placeholder"
                        )}
                    />
                    <CommandList>
                        <CommandEmpty>
                            {t("storage.folder.settings-modal.preset.empty")}
                        </CommandEmpty>
                        <CommandGroup>
                            <CommandItem
                                value="__none__"
                                onSelect={() => {
                                    onChange(null);
                                    setOpen(false);
                                }}
                            >
                                <Check
                                    className={cn(
                                        "me-2 size-4 shrink-0",
                                        value === null
                                            ? "opacity-100"
                                            : "opacity-0"
                                    )}
                                />
                                <CircleOff className="me-1.5 size-3.5 text-muted-foreground" />
                                {t("storage.folder.settings-modal.preset.none")}
                            </CommandItem>
                            {presets?.map((preset) => (
                                <CommandItem
                                    key={preset.id}
                                    value={`${preset.name} ${preset.id}`}
                                    onSelect={() => {
                                        onChange(preset.id);
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            "me-2 size-4 shrink-0",
                                            value === preset.id
                                                ? "opacity-100"
                                                : "opacity-0"
                                        )}
                                    />
                                    <span className="truncate">
                                        {preset.name}
                                    </span>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
